import React, { useState } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import { getImageUrl } from "../api/client";
import { Plus, X, Upload, Clock, User, Calendar, Building, ExternalLink, FileText, CheckCircle2, AlertCircle } from "lucide-react";

const DEPARTMENTS = ["Mechanical", "Electronics", "Software", "IT", "Client", "Other"];

// Helper to format ISO date strings for display
function formatDisplayDateTime(dateStr) {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  } catch (e) {
    return dateStr;
  }
}

export function IncidentScreen({ emp }) {
  const { addIncident } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Get current local datetime formatted for datetime-local input
  const getLocalNowStr = () => {
    const tzOffset = new Date().getTimezoneOffset() * 60000;
    return new Date(Date.now() - tzOffset).toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    problem_statement: "",
    department: "Mechanical",
    custom_department: "",
    has_solution: false,
    solution: "",
    total_time_taken: "",
    no_solution_reason: "",
    solved_by: emp?.name || "",
    incident_date: getLocalNowStr(),
    solution_date: getLocalNowStr(),
    photo: null,
  });

  const incidents = emp.incidents || [];
  const solvedCount = incidents.filter(i => i.has_solution === 1 || i.has_solution === true).length;
  const unsolvedCount = incidents.length - solvedCount;

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, photo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.problem_statement) {
      alert("Please specify the problem statement.");
      return;
    }

    const finalDepartment = form.department === "Other" ? form.custom_department || "Other" : form.department;
    
    const request = {
      problem_statement: form.problem_statement,
      department: finalDepartment,
      has_solution: form.has_solution,
      solution: form.has_solution ? form.solution : null,
      total_time_taken: form.has_solution ? form.total_time_taken : null,
      no_solution_reason: !form.has_solution ? form.no_solution_reason : null,
      solved_by: form.has_solution ? form.solved_by : null,
      incident_date: form.incident_date,
      solution_date: form.has_solution ? form.solution_date : null,
      photo: form.photo
    };

    try {
      await addIncident(request);
      setShowForm(false);
      // Reset form
      setForm({
        problem_statement: "",
        department: "Mechanical",
        custom_department: "",
        has_solution: false,
        solution: "",
        total_time_taken: "",
        no_solution_reason: "",
        solved_by: emp?.name || "",
        incident_date: getLocalNowStr(),
        solution_date: getLocalNowStr(),
        photo: null,
      });
    } catch (err) {
      alert(err.message || "Failed to submit incident");
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div>
      {/* Summary Stats Card Grid */}
      <div style={{ display: "flex", gap: 10 }}>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.navyDeep }}>
            {incidents.length}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>TOTAL INCIDENTS</div>
        </Card>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.success }}>
            {solvedCount}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>SOLVED</div>
        </Card>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.danger }}>
            {unsolvedCount}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>UNSOLVED</div>
        </Card>
      </div>

      {/* Register Incident Button */}
      <button onClick={() => setShowForm(true)} style={{
        marginTop: 14, width: "100%", background: TOKENS.gold, color: TOKENS.navyDeep,
        border: "none", borderRadius: 13, padding: "13px 16px",
        fontWeight: 700, fontSize: 13.5,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        cursor: "pointer", boxShadow: "0 2px 8px rgba(201, 162, 39, 0.2)"
      }}>
        <Plus size={17} /> Register New Incident
      </button>

      {/* Submission Form Modal/Card */}
      {showForm && (
        <Card style={{ marginTop: 14, border: `2px solid ${TOKENS.gold}40` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: `1px solid ${TOKENS.border}`, paddingBottom: 10 }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700, color: TOKENS.navyDeep }}>
              📝 Register New Incident
            </div>
            <button onClick={() => setShowForm(false)} style={{ border: "none", background: "none", cursor: "pointer" }}>
              <X size={18} color={TOKENS.muted} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Problem Statement */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              PROBLEM STATEMENT *
            </label>
            <textarea
              placeholder="Describe the incident/problem in detail…"
              value={form.problem_statement}
              onChange={(e) => setForm((f) => ({ ...f, problem_statement: e.target.value }))}
              rows={3}
              required
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5, color: TOKENS.ink,
                outline: "none", background: TOKENS.cream, resize: "none", marginBottom: 14,
              }}
            />

            {/* Department Selection */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              DEPARTMENT / AREA *
            </label>
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              {DEPARTMENTS.map((dept) => (
                <button
                  type="button"
                  key={dept}
                  onClick={() => setForm((f) => ({ ...f, department: dept }))}
                  style={{
                    padding: "6px 12px", borderRadius: 10,
                    border: `1.5px solid ${form.department === dept ? TOKENS.navyDeep : TOKENS.border}`,
                    background: form.department === dept ? TOKENS.navyDeep : "#fff",
                    color: form.department === dept ? "#fff" : TOKENS.ink,
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {dept}
                </button>
              ))}
            </div>

            {/* Custom Department Name (If Other is selected) */}
            {form.department === "Other" && (
              <input
                type="text"
                placeholder="Type department name…"
                value={form.custom_department}
                onChange={(e) => setForm((f) => ({ ...f, custom_department: e.target.value }))}
                required
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 10,
                  border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                  outline: "none", background: TOKENS.cream, marginBottom: 14,
                }}
              />
            )}

            {/* Incident Date & Time */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              DATE & TIME OF INCIDENT
            </label>
            <input
              type="datetime-local"
              value={form.incident_date}
              onChange={(e) => setForm((f) => ({ ...f, incident_date: e.target.value }))}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                outline: "none", background: TOKENS.cream, marginBottom: 14,
              }}
            />

            {/* Solution Found - Yes / No */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              SOLUTION FOUND?
            </label>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, has_solution: true }))}
                style={{
                  flex: 1, padding: "10px", borderRadius: 10,
                  border: `1.5px solid ${form.has_solution ? TOKENS.success : TOKENS.border}`,
                  background: form.has_solution ? TOKENS.successBg : "#fff",
                  color: form.has_solution ? TOKENS.success : TOKENS.ink,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}
              >
                ✓ Yes
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, has_solution: false }))}
                style={{
                  flex: 1, padding: "10px", borderRadius: 10,
                  border: `1.5px solid ${!form.has_solution ? TOKENS.danger : TOKENS.border}`,
                  background: !form.has_solution ? TOKENS.dangerBg : "#fff",
                  color: !form.has_solution ? TOKENS.danger : TOKENS.ink,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}
              >
                ✗ No
              </button>
            </div>

            {/* Solution Found Conditionals */}
            {form.has_solution ? (
              <div style={{ background: `${TOKENS.successBg}50`, padding: 12, borderRadius: 12, border: `1px solid ${TOKENS.border}`, marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
                  SOLUTION DETAILS *
                </label>
                <textarea
                  placeholder="What is the solution details?"
                  value={form.solution}
                  onChange={(e) => setForm((f) => ({ ...f, solution: e.target.value }))}
                  rows={2}
                  required
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 10,
                    border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                    outline: "none", background: "#fff", resize: "none", marginBottom: 12,
                  }}
                />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
                      TOTAL TIME TAKEN
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 2 hours / 45 mins"
                      value={form.total_time_taken}
                      onChange={(e) => setForm((f) => ({ ...f, total_time_taken: e.target.value }))}
                      style={{
                        width: "100%", padding: "10px 12px", borderRadius: 10,
                        border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                        outline: "none", background: "#fff",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
                      SOLVED BY
                    </label>
                    <input
                      type="text"
                      placeholder="Name of resolver"
                      value={form.solved_by}
                      onChange={(e) => setForm((f) => ({ ...f, solved_by: e.target.value }))}
                      style={{
                        width: "100%", padding: "10px 12px", borderRadius: 10,
                        border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                        outline: "none", background: "#fff",
                      }}
                    />
                  </div>
                </div>

                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
                  SOLUTION TIME AND DATE
                </label>
                <input
                  type="datetime-local"
                  value={form.solution_date}
                  onChange={(e) => setForm((f) => ({ ...f, solution_date: e.target.value }))}
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 10,
                    border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                    outline: "none", background: "#fff",
                  }}
                />
              </div>
            ) : (
              <div style={{ background: `${TOKENS.dangerBg}50`, padding: 12, borderRadius: 12, border: `1px solid ${TOKENS.border}`, marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
                  WHY AND WHAT IS PREVENTING A SOLUTION? *
                </label>
                <textarea
                  placeholder="Explain roadblocks, missing parts, or reasons for not finding a solution…"
                  value={form.no_solution_reason}
                  onChange={(e) => setForm((f) => ({ ...f, no_solution_reason: e.target.value }))}
                  rows={2}
                  required
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 10,
                    border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink,
                    outline: "none", background: "#fff", resize: "none",
                  }}
                />
              </div>
            )}

            {/* Photo Upload */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              PHOTO OF INCIDENT
            </label>
            <label style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer",
              background: TOKENS.cream, border: `1.5px dashed ${TOKENS.border}`,
              borderRadius: 12, padding: "16px 14px", marginBottom: 16, transition: "all 0.2s"
            }}>
              {form.photo ? (
                <div style={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <img src={form.photo} alt="Preview" style={{ maxHeight: 160, maxWidth: "100%", borderRadius: 8, objectFit: "contain", border: `1px solid ${TOKENS.border}` }} />
                  <span style={{ fontSize: 11, color: TOKENS.success, fontWeight: 700, marginTop: 8 }}>✓ Photo attached (Click to change)</span>
                </div>
              ) : (
                <>
                  <Upload size={22} color={TOKENS.muted} />
                  <span style={{ fontSize: 12.5, color: TOKENS.muted, fontWeight: 600 }}>
                    Upload incident photo (Click to snap / select)
                  </span>
                </>
              )}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
            </label>

            <button type="submit" style={{
              width: "100%", background: TOKENS.navyDeep, color: "#fff",
              border: "none", borderRadius: 12, padding: "12px 16px",
              fontWeight: 700, fontSize: 13.5, cursor: "pointer",
              boxShadow: "0 4px 10px rgba(10,31,58,0.2)"
            }}>
              Submit Incident Register
            </button>
          </form>
        </Card>
      )}

      {/* Incident List */}
      <SectionLabel>Incident Logs</SectionLabel>
      {incidents.length === 0 ? (
        <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 32 }}>
          <AlertCircle size={28} color={TOKENS.border} style={{ margin: "0 auto 8px" }} />
          No incidents registered yet.
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {incidents.map((inc) => {
            const isSolved = inc.has_solution === 1 || inc.has_solution === true;
            const isExpanded = expandedId === inc.id;

            return (
              <Card key={inc.id} style={{ padding: 14, cursor: "pointer", borderLeft: `4px solid ${isSolved ? TOKENS.success : TOKENS.danger}` }} onClick={() => toggleExpand(inc.id)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                      <Building size={14} color={TOKENS.muted} />
                      <span style={{ fontWeight: 700, fontSize: 13.5, color: TOKENS.navyDeep }}>
                        {inc.department} Department
                      </span>
                      <span style={{ fontSize: 11, color: TOKENS.muted, display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <Clock size={11} /> {formatDisplayDateTime(inc.incident_date)}
                      </span>
                    </div>

                    <div style={{
                      fontSize: 13,
                      color: TOKENS.ink,
                      lineHeight: 1.45,
                      fontWeight: 500,
                      overflow: isExpanded ? "visible" : "hidden",
                      textOverflow: isExpanded ? "clip" : "ellipsis",
                      whiteSpace: isExpanded ? "pre-wrap" : "nowrap"
                    }}>
                      {inc.problem_statement}
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                    <StatusPill status={isSolved ? "solved" : "unsolved"} />
                    <span style={{ fontSize: 10, color: TOKENS.muted, marginTop: 6 }}>
                      {isExpanded ? "Click to collapse" : "Click to view details"}
                    </span>
                  </div>
                </div>

                {/* Expanded Details Panel */}
                {isExpanded && (
                  <div style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: `1px solid ${TOKENS.border}`,
                    fontSize: 12.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    animation: "fadeIn 0.2s ease"
                  }} onClick={(e) => e.stopPropagation() /* Prevent collapsing when clicking inside expanded panel */}>
                    
                    {/* Roadblock details (if unsolved) */}
                    {!isSolved && inc.no_solution_reason && (
                      <div style={{ background: TOKENS.dangerBg, padding: 10, borderRadius: 10, border: `1px solid ${TOKENS.danger}30` }}>
                        <div style={{ fontWeight: 700, color: TOKENS.danger, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                          <AlertCircle size={14} /> Obstacle / Roadblock Details:
                        </div>
                        <div style={{ color: TOKENS.ink, lineHeight: 1.4 }}>{inc.no_solution_reason}</div>
                      </div>
                    )}

                    {/* Solution details (if solved) */}
                    {isSolved && (
                      <div style={{ background: TOKENS.successBg, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.success}30`, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div>
                          <div style={{ fontWeight: 700, color: TOKENS.success, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                            <CheckCircle2 size={14} /> Solved Details:
                          </div>
                          <div style={{ color: TOKENS.ink, lineHeight: 1.4 }}>{inc.solution}</div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, borderTop: `1px solid ${TOKENS.success}20`, paddingTop: 8, marginTop: 4, fontSize: 11.5 }}>
                          <div>
                            <span style={{ color: TOKENS.muted, fontWeight: 600 }}>Solved By:</span>
                            <div style={{ color: TOKENS.ink, fontWeight: 700, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                              <User size={12} color={TOKENS.muted} /> {inc.solved_by || "Unknown"}
                            </div>
                          </div>
                          <div>
                            <span style={{ color: TOKENS.muted, fontWeight: 600 }}>Total Time Taken:</span>
                            <div style={{ color: TOKENS.ink, fontWeight: 700, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                              <Clock size={12} color={TOKENS.muted} /> {inc.total_time_taken || "N/A"}
                            </div>
                          </div>
                        </div>

                        {inc.solution_date && (
                          <div style={{ fontSize: 11, color: TOKENS.muted, borderTop: `1px solid ${TOKENS.success}10`, paddingTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                            <Calendar size={12} /> Solution resolved at: <strong>{formatDisplayDateTime(inc.solution_date)}</strong>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Incident Attachment Photo */}
                    {inc.photoUrl && (
                      <div style={{ marginTop: 4 }}>
                        <div style={{ fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 6 }}>📷 Incident Attachment:</div>
                        <a href={getImageUrl(inc.photoUrl)} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block" }}>
                          <img
                            src={getImageUrl(inc.photoUrl)}
                            alt="Incident photo"
                            style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, objectFit: "contain" }}
                          />
                        </a>
                        <div style={{ marginTop: 4 }}>
                          <a href={getImageUrl(inc.photoUrl)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: TOKENS.navyDeep, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                            <ExternalLink size={12} /> Open Full Size Photo
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
